import {
  AdminSpinner,
  AllProvidersModal,
  AreaTooltip,
  ChartDataField,
  GroupByType,
  OrdersOverviewEntity,
  Div,
  P,
  ReusableChart,
  formatDateByGroupByTypeChart,
  lightTheme,
  ComparedOrdersOverviewEntity,
  ComparedPeriodType,
  renderPercentageChange,
  calculatePercentageChange,
  NumberType,
  formatAmount,
  DragScrollX,
} from '@vgrubs/components';
import { Dispatch, SetStateAction } from 'react';

//TODO MAKE REUSABLE
const CustomXAxisTick = ({
  x,
  y,
  payload,
  groupByType,
}: {
  x: number;
  y: number;
  payload: { value: string };
  groupByType: GroupByType;
}) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={14}
        textAnchor="middle"
        fontSize="12px"
        fill={lightTheme.lightGray}
      >
        {formatDateByGroupByTypeChart(payload.value, groupByType)}
      </text>
    </g>
  );
};
//TODO MAKE REUSABLE
// Custom tooltip components
const ChartTooltip = ({
  groupByType,
  currency,
  label,
  payload,
  textColor,
  isCancelled,
}: {
  groupByType: GroupByType;
  currency: NumberType;
  label: string;
  payload: any;
  textColor?: string;
  isCancelled?: boolean;
}) => {
  const firstDataKey = payload?.[0]?.dataKey.split('.')[0];
  const secondDataKey = payload?.[1]?.dataKey.split('.')[0];

  const firstDate = payload?.[0]?.payload?.[firstDataKey]?.key;
  const secondDate = payload?.[1]?.payload?.[secondDataKey]?.key;

  const firstValue = payload?.[0]?.value;
  const secondValue = payload?.[1]?.value;

  const calculatedPercentage = calculatePercentageChange(
    firstValue as number,
    secondValue as number
  );

  return (
    <AreaTooltip>
      <Div $flex $center $gap="8px">
        <div>
          {firstDate && (
            <P $m="0">
              {formatDateByGroupByTypeChart(firstDate, groupByType, true)}
              {': '}
              <span style={{ color: textColor }}>
                {formatAmount(firstValue as number, currency)}
              </span>
            </P>
          )}
          {secondDate && (
            <P $m="0">
              {formatDateByGroupByTypeChart(secondDate, groupByType, true)}
              {': '}
              <span style={{ color: lightTheme.darkGray }}>
                {formatAmount(secondValue as number, currency)}
              </span>
            </P>
          )}
        </div>
        {firstDate &&
          secondDate &&
          renderPercentageChange(calculatedPercentage, isCancelled || false)}
      </Div>
    </AreaTooltip>
  );
};

//TODO MAKE REUSABLE
// Custom Y-axis tick for currency formatting
const CurrencyYAxisTick = ({ x, y, payload, currency }: any) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={-5}
      y={-13}
      dy={16}
      fontSize="14px"
      textAnchor="end"
      fill={lightTheme.lightGray}
    >
      {formatAmount(payload.value, NumberType.USD, true)}
    </text>
  </g>
);

// Helper function to create chart fields
const createChartField = (
  dataKey: string,
  color: string,
  options: Partial<ChartDataField> = {}
): ChartDataField => ({
  dataKey,
  color,
  strokeWidth: 2,
  fillOpacity: 1,
  strokeOpacity: 1,
  ...options,
});

export const OrdersCharts = ({
  data,
  comparedData,
  loading,
  setAllProvidersModal,
  groupByType = GroupByType.DAY,
  periodTypeComparedTo,
}: {
  setAllProvidersModal?: Dispatch<SetStateAction<AllProvidersModal | null>>;
  data?: OrdersOverviewEntity | null;
  comparedData?: ComparedOrdersOverviewEntity | null;
  loading?: boolean;
  groupByType?: GroupByType;
  periodTypeComparedTo?: ComparedPeriodType;
}) => {
  const isComparing =
    periodTypeComparedTo === ComparedPeriodType.LAST_MONTH ||
    periodTypeComparedTo === ComparedPeriodType.LAST_YEAR ||
    periodTypeComparedTo === ComparedPeriodType.CUSTOM;

  const compareToTitle = (title: string) => {
    if (periodTypeComparedTo === ComparedPeriodType.LAST_MONTH) {
      return `${title} Last Month`;
    } else if (periodTypeComparedTo === ComparedPeriodType.LAST_YEAR) {
      return `${title} Last Year`;
    } else if (periodTypeComparedTo === ComparedPeriodType.CUSTOM) {
      return `${title} Custom Date Range`;
    }
    return '';
  };

  return (
    <Div $relative>
      {loading && (
        <AdminSpinner small absolute overflowBorderRadius="20px" creme />
      )}
      <DragScrollX>
        {/* Total Sales Chart */}

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Sales"
          titleCompare={compareToTitle('Sales')}
          currency={NumberType.USD}
          totalValue={
            isComparing
              ? comparedData?.totalSalesSum || 0
              : data?.totalSalesSum || 0
          }
          totalValueToCompare={comparedData?.totalSalesSumToCompare || 0}
          valueColor={lightTheme.green}
          data={data?.totalSales || []}
          dataToCompare={comparedData?.comparedTotalSales}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel={'Total Sales'}
          chartFields={[
            createChartField('totalSales', lightTheme.green, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('totalSales.totalSales', lightTheme.green, {
              stackId: '2',
            }),
            createChartField(
              'totalSalesToCompare.totalSales',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalSalesByProvider &&
            data.totalSalesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalSalesSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalSalesByProvider &&
            comparedData.comparedTotalSalesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalSalesSum,
                totalValueToCompare: item.totalSalesSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        {/* Total Orders Chart */}

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Orders"
          titleCompare={compareToTitle('Orders')}
          totalValue={
            isComparing
              ? comparedData?.totalOrdersCount || 0
              : data?.totalOrdersCount || 0
          }
          currency={NumberType.NUMBER}
          totalValueToCompare={comparedData?.totalOrdersCountToCompare || 0}
          valueColor={lightTheme.green}
          data={data?.totalOrders || []}
          dataToCompare={comparedData?.comparedTotalOrders}
          groupByType={groupByType}
          chartFields={[
            createChartField('totalOrders', lightTheme.green, {
              radius: [10, 10, 0, 0],
              stackId: 'a',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('totalOrders.totalOrders', lightTheme.green, {
              radius: [10, 10, 0, 0],
              stackId: '2',
            }),
            createChartField(
              'totalOrdersToCompare.totalOrders',
              lightTheme.darkGray,
              {
                radius: [10, 10, 0, 0],
                stackId: '1',
              }
            ),
          ]}
          modalLabel="Total Orders"
          setAllProvidersModal={setAllProvidersModal}
          dataByProvider={
            data?.totalOrdersByProvider &&
            data.totalOrdersByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalOrdersCount,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalOrdersByProvider &&
            comparedData.comparedTotalOrdersByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalOrdersCount,
                totalValueToCompare: item.totalOrdersCountToCompare,
              };
            })
          }
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        {/* Total Taxes Chart */}

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Taxes"
          titleCompare={compareToTitle('Taxes')}
          totalValue={
            isComparing
              ? comparedData?.totalTaxesSum || 0
              : data?.totalTaxesSum || 0
          }
          currency={NumberType.USD}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel="Total Taxes"
          totalValueToCompare={comparedData?.totalTaxesSumToCompare || 0}
          dataByProvider={
            data?.totalTaxesByProvider &&
            data.totalTaxesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalTaxesSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalTaxesByProvider &&
            comparedData.comparedTotalTaxesByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalTaxesSum,
                totalValueToCompare: item.totalTaxesSumToCompare,
              };
            })
          }
          valueColor={lightTheme.orange}
          data={data?.totalTaxes || []}
          dataToCompare={comparedData?.comparedTotalTaxes}
          groupByType={groupByType}
          chartFields={[
            createChartField('totalTaxes', lightTheme.orange, {
              radius: [10, 10, 0, 0],
              stackId: 'a',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField('totalTaxes.totalTaxes', lightTheme.orange, {
              radius: [10, 10, 0, 0],
              stackId: '2',
            }),
            createChartField(
              'totalTaxesToCompare.totalTaxes',
              lightTheme.darkGray,
              {
                radius: [10, 10, 0, 0],
                stackId: '1',
              }
            ),
          ]}
          customYAxisTick={CurrencyYAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        {/* Total Recovered Chart */}

        <ReusableChart
          isComparing={isComparing}
          chartType="area"
          title="Total Recovered"
          titleCompare={compareToTitle('Recovered')}
          totalValue={
            isComparing
              ? comparedData?.totalRecoveredSum || 0
              : data?.totalRecoveredSum || 0
          }
          currency={NumberType.USD}
          valueColor={lightTheme.darkYellow}
          data={data?.totalRecovered || []}
          dataToCompare={comparedData?.comparedTotalRecovered}
          groupByType={groupByType}
          setAllProvidersModal={setAllProvidersModal}
          modalLabel="Total Recovered"
          totalValueToCompare={comparedData?.totalRecoveredSumToCompare || 0}
          chartFields={[
            createChartField('totalRecovered', lightTheme.darkYellow, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField(
              'totalRecovered.totalRecovered',
              lightTheme.darkYellow,
              {
                stackId: '2',
              }
            ),
            createChartField(
              'totalRecoveredToCompare.totalRecovered',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          dataByProvider={
            data?.totalRecoveredByProvider &&
            data.totalRecoveredByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalRecoveredSum,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalRecoveredByProvider &&
            comparedData.comparedTotalRecoveredByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalRecoveredSum,
                totalValueToCompare: item.totalRecoveredSumToCompare,
              };
            })
          }
          customYAxisTick={CurrencyYAxisTick}
          customXAxisTick={CustomXAxisTick}
          customTooltip={isComparing ? ChartTooltip : undefined}
        />

        {/* Total Cancelled Chart */}

        <ReusableChart
          currency={NumberType.NUMBER}
          isComparing={isComparing}
          chartType="area"
          title="Total Cancelled"
          modalLabel="Total Cancelled"
          titleCompare={compareToTitle('Cancelled')}
          totalValue={
            isComparing
              ? comparedData?.totalCancelledCount || 0
              : data?.totalCancelledCount || 0
          }
          valueColor={lightTheme.primary}
          data={data?.totalCancelled || []}
          dataToCompare={comparedData?.comparedTotalCancelled}
          groupByType={groupByType}
          chartFields={[
            createChartField('totalCancelled', lightTheme.primary, {
              stackId: '1',
            }),
          ]}
          chartFieldsToCompare={[
            createChartField(
              'totalCancelled.totalCancelled',
              lightTheme.primary,
              {
                stackId: '2',
              }
            ),
            createChartField(
              'totalCancelledToCompare.totalCancelled',
              lightTheme.darkGray,
              {
                stackId: '1',
              }
            ),
          ]}
          setAllProvidersModal={setAllProvidersModal}
          totalValueToCompare={comparedData?.totalCancelledCountToCompare || 0}
          dataByProvider={
            data?.totalCancelledByProvider &&
            data.totalCancelledByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalCancelledCount,
                totalValueToCompare: null,
              };
            })
          }
          dataByProviderToCompare={
            comparedData?.comparedTotalCancelledByProvider &&
            comparedData.comparedTotalCancelledByProvider.map((item) => {
              return {
                provider: item.provider,
                totalValue: item.totalCancelledCount,
                totalValueToCompare: item.totalCancelledCountToCompare,
              };
            })
          }
          customTooltip={isComparing ? ChartTooltip : undefined}
        />
      </DragScrollX>
    </Div>
  );
};
